import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
} from '@mui/material';
import { Search as SearchIcon, Download as DownloadIcon } from '@mui/icons-material';
import { reportService, TimeTrackerReportEntry, TimeTrackerReportFilters } from '../services/reportService';
import { useAuth } from '../contexts/AuthContext';

const TimeTrackerReport: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check if user is authorized
  // Backend handles filtering - PMP admins, Site Admin, and Client Manager see all, others see their accessible firms
  const isAuthorized = () => {
    if (!user) return false;
    // PMP Admin, Site Admin, or Client Manager can always access
    if (user.roles && user.roles.some(role => 
      ['pmp_admin', 'site_admin', 'client_manager', 'internal_manager'].includes(role.slug)
    )) {
      return true;
    }
    // Other users can access if they have a firm association (backend will filter appropriately)
    return !!(user.firm_id || user.firm);
  };

  useEffect(() => {
    if (user && !isAuthorized()) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // Get today's date and 30 days ago as defaults
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const formatDate = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  // Filter state
  const [clientFirmId, setClientFirmId] = useState<number | ''>('');
  const [userId, setUserId] = useState<number | ''>('');
  const [startDate, setStartDate] = useState<string>(formatDate(thirtyDaysAgo));
  const [endDate, setEndDate] = useState<string>(formatDate(today));
  const [selectedActivities, setSelectedActivities] = useState<string[]>([]);
  const [selectedDispositions, setSelectedDispositions] = useState<string[]>([]);

  // Filter options
  const [filters, setFilters] = useState<TimeTrackerReportFilters>({
    client_firms: [],
    users: [],
    activities: [],
    dispositions: [],
  });

  // Data state
  const [reportData, setReportData] = useState<TimeTrackerReportEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Load filter options on mount
  useEffect(() => {
    if (isAuthorized()) {
      loadFilters();
    }
  }, [user]);

  const loadFilters = async () => {
    try {
      const filterData = await reportService.getTimeTrackerReportFilters();
      setFilters(filterData);
    } catch (err: any) {
      console.error('Failed to load filters:', err);
      setError('Failed to load filter options');
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = {};
      
      if (clientFirmId) {
        params.client_firm_id = clientFirmId;
      }
      if (userId) {
        params.user_id = userId;
      }
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }
      if (selectedActivities.length > 0) {
        params.activity = selectedActivities;
      }
      if (selectedDispositions.length > 0) {
        params.disposition = selectedDispositions;
      }

      const data = await reportService.getTimeTrackerReport(params);
      setReportData(data as TimeTrackerReportEntry[]);
      setPage(0); // Reset to first page
    } catch (err: any) {
      console.error('Error fetching report:', err);
      setError(err.response?.data?.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'csv' | 'excel' = 'csv') => {
    setExporting(true);
    setError('');
    try {
      const params: any = {
        export: format,
      };
      
      if (clientFirmId) {
        params.client_firm_id = clientFirmId;
      }
      if (userId) {
        params.user_id = userId;
      }
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }
      if (selectedActivities.length > 0) {
        params.activity = selectedActivities;
      }
      if (selectedDispositions.length > 0) {
        params.disposition = selectedDispositions;
      }

      const blob = await reportService.getTimeTrackerReport(params) as Blob;
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = `time-tracker-report_${startDate || 'all'}_to_${endDate || 'all'}.${format === 'csv' ? 'csv' : 'xlsx'}`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error exporting report:', err);
      setError(err.response?.data?.message || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate pagination
  const paginatedData = reportData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (!isAuthorized()) {
    return (
      <Box>
        <Alert severity="error">
          You do not have permission to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Time Tracker Report
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filter
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
              <FormControl fullWidth>
                <InputLabel>Client List</InputLabel>
                <Select
                  value={clientFirmId}
                  label="Client List"
                  onChange={(e) => setClientFirmId(e.target.value as number | '')}
                >
                  <MenuItem value="">All</MenuItem>
                  {filters.client_firms.map((firm) => (
                    <MenuItem key={firm.id} value={firm.id}>
                      {firm.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
              <FormControl fullWidth>
                <InputLabel>User List</InputLabel>
                <Select
                  value={userId}
                  label="User List"
                  onChange={(e) => setUserId(e.target.value as number | '')}
                >
                  <MenuItem value="">--Choose User--</MenuItem>
                  {filters.users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '150px' } }}>
              <TextField
                fullWidth
                label="Start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '150px' } }}>
              <TextField
                fullWidth
                label="End"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '250px' } }}>
              <Autocomplete
                multiple
                options={filters.activities.map(a => a.value)}
                getOptionLabel={(option) => {
                  const activity = filters.activities.find(a => a.value === option);
                  return activity ? activity.name : option;
                }}
                value={selectedActivities}
                onChange={(event, newValue) => {
                  setSelectedActivities(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Activity"
                    placeholder="Select activities"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const activity = filters.activities.find(a => a.value === option);
                    return (
                      <Chip
                        label={activity ? activity.name : option}
                        {...getTagProps({ index })}
                        key={option}
                      />
                    );
                  })
                }
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '250px' } }}>
              <Autocomplete
                multiple
                options={filters.dispositions.map(d => d.value)}
                getOptionLabel={(option) => {
                  const disposition = filters.dispositions.find(d => d.value === option);
                  return disposition ? disposition.name : option;
                }}
                value={selectedDispositions}
                onChange={(event, newValue) => {
                  setSelectedDispositions(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Dispositions"
                    placeholder="Select dispositions"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const disposition = filters.dispositions.find(d => d.value === option);
                    return (
                      <Chip
                        label={disposition ? disposition.name : option}
                        {...getTagProps({ index })}
                        key={option}
                      />
                    );
                  })
                }
              />
            </Box>
            <Box sx={{ minWidth: { xs: '100%', md: '120px' } }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                sx={{ height: '56px' }}
              >
                Submit
              </Button>
            </Box>
            <Box sx={{ minWidth: { xs: '100%', md: '120px' } }}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                onClick={() => handleExport('csv')}
                disabled={exporting}
                startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
                sx={{ height: '56px' }}
              >
                Export CSV
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {reportData.length > 0 && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Result ({reportData.length} entries)
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Client</strong></TableCell>
                  <TableCell><strong>Timer ID</strong></TableCell>
                  <TableCell><strong>Case ID</strong></TableCell>
                  <TableCell><strong>Case Name</strong></TableCell>
                  <TableCell><strong>File Number</strong></TableCell>
                  <TableCell><strong>Activity</strong></TableCell>
                  <TableCell><strong>Disposition</strong></TableCell>
                  <TableCell><strong>User</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Start Time</strong></TableCell>
                  <TableCell><strong>End Time</strong></TableCell>
                  <TableCell><strong>Duration</strong></TableCell>
                  <TableCell><strong>Deduction</strong></TableCell>
                  <TableCell><strong>Net Duration</strong></TableCell>
                  <TableCell><strong>Ref ID</strong></TableCell>
                  <TableCell><strong>Note</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((entry) => (
                  <TableRow key={entry.timer_id} hover>
                    <TableCell>{entry.client}</TableCell>
                    <TableCell>{entry.timer_id}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => {
                          if (entry.client_firm_id) {
                            navigate(`/firms/${entry.client_firm_id}/cases/${entry.case_id}`);
                          } else {
                            navigate(`/cases/${entry.case_id}`);
                          }
                        }}
                      >
                        {entry.case_id}
                      </Button>
                    </TableCell>
                    <TableCell>{entry.case_name}</TableCell>
                    <TableCell>{entry.file_number || '-'}</TableCell>
                    <TableCell>{entry.activity_name}</TableCell>
                    <TableCell>{entry.disposition}</TableCell>
                    <TableCell>{entry.user}</TableCell>
                    <TableCell>{entry.date_formatted}</TableCell>
                    <TableCell>{entry.start_time}</TableCell>
                    <TableCell>{entry.end_time}</TableCell>
                    <TableCell>{entry.duration_formatted}</TableCell>
                    <TableCell>{entry.deduction_formatted}</TableCell>
                    <TableCell>{entry.net_duration_formatted}</TableCell>
                    <TableCell>{entry.ref_id || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {entry.note || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={reportData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </Paper>
      )}
    </Box>
  );
};

export default TimeTrackerReport;
